import { graphqlRequest } from "@/graphql/client";
import * as graphQlMutations from "../../graphql/mutations";
import * as graphQlQueries from "../../graphql/queries";
import { DEFAULT_UNKNOWN } from "@/constants/propertyFieldDefaults";
import { extractPropertyDetails } from "@/utils/extractPropertyCoreFields";
import { sanitizeCalculationMedia } from "@/utils/maoCalculator/sanitizeCalculationMedia";
import { IS_DEV, SUPABASE_API_KEY, SUPABASE_SEARCH_INVESTORS_URL } from "@/utils/env";
import { extractBodyString, batchFetchPropertiesByCustomer } from "../propertyCore/propertyApi";
import { sanitizePropertyIdForBatch, mergeDealWithProperty } from "./dealPropertyUtils";
import { normalizeTimestamp } from "@/utils/unifiedEmail";

/**
 * Resolves the generated `getAllDealLens` GraphQL query from generated artifacts.
 *
 * @returns {string} GraphQL query document
 * @throws {Error} When generated artifacts do not include getAllDealLens yet
 */
function getAllDealLensQueryDocument() {
  const queryDocument = graphQlQueries?.getAllDealLens;
  if (typeof queryDocument !== "string" || queryDocument.length === 0) {
    throw new Error("Missing generated getAllDealLens query. Run `amplify push` and regenerate.");
  }
  return queryDocument;
}

/**
 * Safely parses JSON if input is a string.
 *
 * @param {*} value - Incoming value from API payload
 * @returns {*} Parsed value or original input if parsing fails
 */
function safeParseJson(value) {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

/**
 * Parses a calculation payload that may arrive as an object or JSON string.
 *
 * @param {*} calculation - Raw calculation payload
 * @returns {object|null} Parsed calculation object or null
 */
function parseCalculation(calculation) {
  const parsed = safeParseJson(calculation);
  if (!parsed || typeof parsed !== "object") return null;
  return sanitizeCalculationMedia(parsed);
}

/**
 * Extracts item collection from the DealLens response body.
 *
 * @param {object} parsedBody - Parsed response body
 * @returns {Array<object>} Normalized list of raw deal rows
 */
function extractDealRows(parsedBody) {
  if (Array.isArray(parsedBody?.items)) return parsedBody.items;
  if (Array.isArray(parsedBody?.data)) return parsedBody.data;
  if (Array.isArray(parsedBody?.deals)) return parsedBody.deals;
  return [];
}

/**
 * Normalizes a DynamoDB LastEvaluatedKey cursor from a response body.
 * Accepts object or JSON-string forms; returns the original object/string for round-trip.
 *
 * @param {*} value
 * @returns {object|string|null}
 */
function normalizeNextPageCursor(value) {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === "object") return parsed;
    } catch {
      // Opaque string cursor — round-trip as-is.
    }
    return trimmed;
  }
  if (typeof value === "object") return value;
  return null;
}

/**
 * Extracts pagination metadata from a DealLens body payload.
 *
 * @param {object} parsedBody - Parsed response body
 * @returns {{has_next: boolean, next_page: object|null}} Normalized pagination payload
 */
function extractPagination(parsedBody) {
  const directNextPage = parsedBody?.next_page ?? parsedBody?.LastEvaluatedKey ?? null;
  const nestedNextPage = parsedBody?.pagination?.next_page ?? null;
  const nextPage = normalizeNextPageCursor(directNextPage || nestedNextPage || null);
  return {
    has_next: Boolean(nextPage),
    next_page: nextPage,
  };
}

/**
 * Normalizes one raw DealLens row into a stable UI/store shape.
 *
 * @param {object} row - Raw row from DealLens API
 * @param {number} index - Row index (used for fallback IDs)
 * @returns {object} Normalized deal object
 */
export function normalizeDealRow(row, index) {
  const propertyId = String(row?.property_id || row?.property || "").trim();
  const dealLensId = String(row?.deallens_id || row?.id || "").trim();
  const id = dealLensId || propertyId || `deal-${Date.now()}-${index}`;
  const calculation = parseCalculation(row?.calculation);
  const metadata = safeParseJson(row?.metadata);
  const customFields = metadata?.custom_fields && typeof metadata.custom_fields === "object"
    ? metadata.custom_fields
    : {};
  const bedValue = Number(row?.bed ?? customFields?.beds ?? customFields?.bedrooms ?? 0) || 0;
  const bathValue = Number(row?.bath ?? customFields?.baths ?? customFields?.bathrooms ?? 0) || 0;
  const sqftValue = Number(row?.sqft ?? customFields?.sq_ft ?? customFields?.square_feet ?? 0) || 0;
  const customFieldAddress =
    customFields?.full_address || customFields?.address || customFields?.property_address || "";

  return {
    id,
    deallens_id: dealLensId || null,
    property_id: propertyId || null,
    sender: row?.sender || "",
    receiver: row?.receiver || "",
    sender_role: row?.sender_role || "",
    receiver_role: row?.receiver_role || "",
    homeowner_email: row?.homeowner_email || null,
    representative: row?.representative || null,
    county: row?.county || "",
    status: row?.status || "",
    active: row?.active === false ? false : true,
    swipe_status: row?.swipe_status || "",
    bed: bedValue,
    bath: bathValue,
    sqft: sqftValue,
    year_built: Number(row?.year_built ?? 0) || 0,
    calculation,
    metadata,
    collection_id:
      typeof row?.collection_id === "string" && row.collection_id.trim()
        ? row.collection_id.trim()
        : null,
    insert_date: normalizeTimestamp(row?.insert_date || row?.created_at || null) ?? row?.insert_date ?? row?.created_at ?? null,
    last_modified: normalizeTimestamp(row?.last_modified || null) ?? row?.last_modified ?? null,
    thread: row?.thread || null,
    full_address: row?.full_address || row?.address || calculation?.address || customFieldAddress || "",
    city: row?.city || customFields?.city || "",
    state: row?.state || customFields?.state || "",
    zipcode: row?.zipcode || customFields?.zipcode || customFields?.zip || "",
    hidden_address: Boolean(row?.hidden_address),
    raw: row,
  };
}

/**
 * Resolves the generated `getPropertyDealLensThread` GraphQL query from generated artifacts.
 *
 * @returns {string} GraphQL query document
 * @throws {Error} When generated artifacts do not include getPropertyDealLensThread yet
 */
function getPropertyDealLensThreadDocument() {
  const queryDocument = graphQlQueries?.getDeallensPropertyThreads;
  if (typeof queryDocument !== "string" || queryDocument.length === 0) {
    throw new Error("Missing generated getDeallensPropertyThreads query. Run `amplify push` and regenerate.");
  }
  return queryDocument;
}

/**
 * Fetches all DealLens thread items for a given property, normalized for offer list consumption.
 * SECURITY: Uses AMAZON_COGNITO_USER_POOLS; requires customer and propertyId from authorized session context.
 *
 * @param {object} params - Request parameters
 * @param {string} params.customer - Customer identifier from auth context
 * @param {string} params.propertyId - Property ID (e.g. "property#...")
 * @param {string} [params.role] - Embodied role for act-as-user / view-scoped thread fetch
 * @returns {Promise<object[]>} Array of normalized deal rows
 * @throws {Error} When required params are missing or the API call fails
 */
export async function fetchPropertyOffers({ customer, propertyId, byCollection = false, role = null }) {
  const safeCustomer = String(customer ?? "").trim();
  const safePropertyId = String(propertyId ?? "").trim();
  const safeRole = String(role ?? "")
    .trim()
    .toLowerCase();

  if (!safeCustomer || !safePropertyId) {
    throw new Error("fetchPropertyOffers: customer and propertyId are required");
  }

  const query = getPropertyDealLensThreadDocument();

  const variables = { customer: safeCustomer, property_id: safePropertyId };
  if (typeof byCollection === "boolean") {
    variables.by_collection = byCollection;
  }
  if (safeRole) {
    variables.role = safeRole;
  }

  const response = await graphqlRequest({
    query,
    variables,
    authMode: "AMAZON_COGNITO_USER_POOLS",
  });

  const apiResult = response?.data?.getDeallensPropertyThreads;
  const statusCode = apiResult?.statusCode ?? null;

  if (Number(statusCode) === 204) {
    return [];
  }

  const rawBody = apiResult?.body;
  const bodyForExtract =
    typeof rawBody === "string"
      ? rawBody
      : typeof rawBody === "object" && rawBody !== null
        ? JSON.stringify(rawBody)
        : "{}";
  const bodyString = extractBodyString(bodyForExtract);
  const parsedBody = safeParseJson(bodyString);
  const rows = extractDealRows(parsedBody);

  if (IS_DEV) {
    console.log("[dealsApi] fetchPropertyOffers RAW parsed body:", parsedBody);
    if (rows.length > 0) {
      console.log("[dealsApi] fetchPropertyOffers first row shape:", rows[0]);
    }
  }

  return rows.map((row, idx) => normalizeDealRow(row, idx));
}

/** One week — investor count cached on `property.additional_attributes`. */
export const INVESTOR_COUNT_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * @param {Date} [fromDate]
 * @returns {string} ISO-8601 expiry stored on the property
 */
export function computeInvestorCountCacheExpiresAt(fromDate = new Date()) {
  return new Date(fromDate.getTime() + INVESTOR_COUNT_CACHE_TTL_MS).toISOString();
}

/**
 * @param {number} investors_count
 * @param {Date} [fromDate]
 * @returns {{ investors_count: number, investors_count_expires_at: string }}
 */
export function buildInvestorCountCacheAttributes(investors_count, fromDate = new Date()) {
  const countNum = Number(investors_count);
  if (!Number.isFinite(countNum)) {
    throw new Error("Invalid investors_count for cache");
  }
  return {
    investors_count: Math.max(0, Math.round(countNum)),
    investors_count_expires_at: computeInvestorCountCacheExpiresAt(fromDate),
  };
}

/**
 * Missing or invalid `investors_count_expires_at` is treated as rotten (same as expired).
 *
 * @param {object|null|undefined} property
 * @param {number} [nowMs=Date.now()]
 * @returns {boolean}
 */
export function isInvestorCountCacheExpired(property, nowMs = Date.now()) {
  const raw = property?.additional_attributes?.investors_count_expires_at;
  if (raw == null || raw === "") return true;
  const expiresMs = Date.parse(String(raw));
  if (!Number.isFinite(expiresMs)) return true;
  return nowMs >= expiresMs;
}

/**
 * Normalizes backend investor-match summary JSON (`investor_count` from search-investors-by-property).
 *
 * @param {*} raw - Parsed API body or object
 * @returns {{
 *   address: string,
 *   investor_count: number,
 *   total_matches: number,
 *   is_approximate: boolean,
 *   approximate_count: number,
 *   investors: object[],
 * }|null} Normalized shape, or null if input is unusable
 */
export function normalizeInvestorMatchSummary(raw) {
  if (raw == null || typeof raw !== "object") return null;
  const investor_count = Number(raw.investor_count);
  const count = Number.isFinite(investor_count) ? Math.max(0, investor_count) : 0;
  return {
    address: raw.address != null ? String(raw.address) : "",
    investor_count: count,
    total_matches: 0,
    is_approximate: count > 0,
    approximate_count: count,
    investors: Array.isArray(raw.investors) ? raw.investors : [],
  };
}

/**
 * Uses cached `investors_count` only when a non-expired `investors_count_expires_at` is present.
 * Count without TTL is ignored so Supabase can refresh count + expiry.
 *
 * @param {object|null|undefined} property
 * @returns {ReturnType<typeof normalizeInvestorMatchSummary>|null}
 */
export function readInvestorMatchSummaryFromProperty(property) {
  const investors_count = property?.additional_attributes?.investors_count;
  if (investors_count == null) return null;
  const count = Number(investors_count);
  if (!Number.isFinite(count)) return null;
  if (isInvestorCountCacheExpired(property)) return null;
  return normalizeInvestorMatchSummary({ investor_count: count });
}

/**
 * Maps listing property type labels to enum-style values expected by search-investors-by-property.
 *
 * @param {string} raw - Raw property type from `additional_attributes.property_type`
 * @returns {string}
 */
function mapPropertyTypeForInvestorApi(raw) {
  const s = String(raw ?? "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_");
  if (!s || s === "N/A" || s === "UNKNOWN") return "SINGLE_FAMILY";
  if (s.includes("SINGLE") && s.includes("FAMILY")) return "SINGLE_FAMILY";
  if (s.includes("MULTI") && s.includes("FAMILY")) return "MULTI_FAMILY";
  if (s.includes("CONDO")) return "CONDO";
  if (s.includes("TOWN")) return "TOWNHOUSE";
  if (/^[A-Z][A-Z0-9_]*$/.test(s) && s.length < 48) return s;
  return "SINGLE_FAMILY";
}

/**
 * Resolves a US state code for investor search when `property.state` is unset.
 *
 * @param {object} property
 * @param {string} address
 * @returns {string}
 */
export function resolveSearchInvestorsState(property, address) {
  const fromField = String(property?.state ?? "").trim();
  if (fromField) return fromField;

  const fullAddress = String(address ?? "").trim();
  const trailingStateZip = fullAddress.match(/,\s*([A-Z]{2})\s+\d{5}(?:-\d{4})?\s*$/i);
  if (trailingStateZip) return trailingStateZip[1].toUpperCase();

  return "";
}

/**
 * Builds the POST body for the Supabase `search-investors-by-property` function.
 *
 * @param {object|null|undefined} property - Property record from PropertyDetailsStore / API
 * @returns {object|null} JSON body, or null when address/state are missing
 */
export function buildSearchInvestorsByPropertyBody(property) {
  if (!property || typeof property !== "object") return null;
  const d = extractPropertyDetails(property);
  const attrs =
    property.additional_attributes && typeof property.additional_attributes === "object"
      ? property.additional_attributes
      : {};
  const lotRaw = attrs.lot_size ?? attrs.lot_square_footage ?? attrs.lotSizeSqft ?? 0;
  const lot_size = Number.isFinite(Number(lotRaw)) ? Math.max(0, Math.round(Number(lotRaw))) : 0;
  const yearParsed = Number(d.yearBuilt);
  const year_built =
    Number.isFinite(yearParsed) && yearParsed > 1600 && yearParsed <= 2100 ? Math.round(yearParsed) : 0;
  const county = d.county && d.county !== DEFAULT_UNKNOWN ? String(d.county).trim() : "";
  const address = String(d.address ?? "").trim();
  const state = resolveSearchInvestorsState(property, address);
  if (!address || !state) return null;
  return {
    address,
    state,
    county,
    property_type: mapPropertyTypeForInvestorApi(d.propertyType),
    price: Math.max(0, Math.round(Number(d.price) || 0)),
    beds: Math.max(0, Math.round(Number(d.beds) || 0)),
    baths: Math.max(0, Number(d.baths) || 0),
    sqft: Math.max(0, Math.round(Number(d.sqft) || 0)),
    year_built,
    lot_size,
  };
}

/**
 * Fetches investor match summary from Supabase Edge (`search-investors-by-property`).
 * Uses `VITE_SUPABASE_SEARCH_INVESTORS_URL` and `VITE_SUPABASE_API_KEY`.
 *
 * @param {object} params
 * @param {object|null|undefined} params.property - Loaded property (for address, attrs, county, etc.)
 * @returns {Promise<object|null>} Normalized summary or null when skipped / failed
 */
export async function fetchPropertyInvestorMatchSummary({ property } = {}) {
  const url = String(SUPABASE_SEARCH_INVESTORS_URL ?? "").trim();
  const apiKey = String(SUPABASE_API_KEY ?? "").trim();
  if (!url || !apiKey) {
    if (IS_DEV) {
      console.warn(
        "[dealsApi] fetchPropertyInvestorMatchSummary skipped: set VITE_SUPABASE_SEARCH_INVESTORS_URL and VITE_SUPABASE_API_KEY"
      );
    }
    return null;
  }
  const body = buildSearchInvestorsByPropertyBody(property);
  if (!body) {
    if (IS_DEV) {
      console.warn("[dealsApi] fetchPropertyInvestorMatchSummary skipped: missing address/state on property");
    }
    return null;
  }
  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": apiKey,
      },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      if (IS_DEV) {
        console.error("[dealsApi] search-investors-by-property HTTP", response.status);
      }
      return null;
    }
    const json = await response.json();
    return normalizeInvestorMatchSummary(json);
  } catch (err) {
    if (IS_DEV) {
      console.error("[dealsApi] fetchPropertyInvestorMatchSummary", err);
    }
    return null;
  }
}

/**
 * Builds DealLens query variables from auth and filter context.
 *
 * @param {object} params - Request parameters
 * @returns {object} GraphQL variables payload
 */
export function buildGetAllDealLensVariables({
  customer,
  userEmail,
  queryRole,
  status = null,
  county = null,
  bed = null,
  bath = null,
  sqftMin = null,
  sqftMax = null,
  yearBuiltMin = null,
  yearBuiltMax = null,
  swipeStatus = null,
  latestPerThread = null,
  nextPage = null,
} = {}) {
  const variables = {
    customer: String(customer ?? "").trim(),
    user_email: String(userEmail || "").trim(),
    query_role: String(queryRole || "").trim(),
  };

  if (status !== null && status !== undefined && status !== "") variables.status = String(status);
  if (county !== null && county !== undefined && county !== "") variables.county = String(county);
  if (Number.isFinite(bed)) variables.bed = Number(bed);
  if (Number.isFinite(bath)) variables.bath = Number(bath);
  if (Number.isFinite(sqftMin)) variables.sqft_min = Number(sqftMin);
  if (Number.isFinite(sqftMax)) variables.sqft_max = Number(sqftMax);
  if (Number.isFinite(yearBuiltMin)) variables.year_built_min = Number(yearBuiltMin);
  if (Number.isFinite(yearBuiltMax)) variables.year_built_max = Number(yearBuiltMax);
  if (swipeStatus !== null && swipeStatus !== undefined && swipeStatus !== "") {
    variables.swipe_status = String(swipeStatus);
  }
  if (typeof latestPerThread === "boolean") variables.latest_per_thread = latestPerThread;
  if (nextPage !== null && nextPage !== undefined && nextPage !== "") {
    variables.next_page = typeof nextPage === "string" ? nextPage : JSON.stringify(nextPage);
  }

  if (!variables.customer || !variables.user_email || !variables.query_role) {
    throw new Error("Missing required DealLens variables: customer, user_email, or query_role");
  }

  return variables;
}

/**
 * Calls the deallensSwipe mutation to update a deal's swipe status.
 * SECURITY: Uses AMAZON_COGNITO_USER_POOLS; caller must supply authorized customer context.
 *
 * @param {Object} params - Swipe parameters
 * @param {string} params.deallensId - DealLens deal id (deallens_id)
 * @param {string} params.sender - Sender email from the deal
 * @param {string} params.receiver - Receiver email from the deal
 * @param {string} params.swipeStatus - One of DEALLENS_SWIPE_STATUS (LIKED, PASSED, UNSWIPED)
 * @param {string} params.customerId - Customer id for authorization
 * @returns {Promise<{ statusCode: number|string|null, body: unknown }>}
 */
export async function deallensSwipe({ deallensId, sender, receiver, swipeStatus, customerId }) {
  const mutation = graphQlMutations?.deallensSwipe;
  if (typeof mutation !== "string" || mutation.length === 0) {
    throw new Error("Missing generated deallensSwipe mutation. Run `amplify push` and regenerate.");
  }

  const response = await graphqlRequest({
    query: mutation,
    variables: {
      deallens_id: String(deallensId ?? "").trim(),
      sender: String(sender ?? "").trim(),
      receiver: String(receiver ?? "").trim(),
      swipe_status: String(swipeStatus ?? "").trim(),
      customer_id: String(customerId ?? "").trim(),
    },
    authMode: "AMAZON_COGNITO_USER_POOLS",
  });

  const apiResult = response?.data?.deallensSwipe ?? null;
  return {
    statusCode: apiResult?.statusCode ?? null,
    body: apiResult?.body ?? null,
  };
}

/**
 * Orchestrates fetching DealLens rows and enriching them with full property details.
 * Provides a way for the caller to receive the raw list first (onRaw), then wait for enrichment (resolved promise).
 * SECURITY: Scopes both deal and property requests to authorized context.
 *
 * @param {object} params - Request parameters (customer, userEmail, queryRole, filters, nextPage, etc.)
 * @param {{ onRaw?: (result: { data: object[], pagination: object, statusCode: string|number|null, isEmpty: boolean }) => void }} [options] - Optional. If onRaw is provided, it is called with the raw result as soon as Phase 1 completes so the caller can render immediately; the promise still resolves with the enriched result (or raw if enrichment fails).
 * @returns {Promise<{data: object[], pagination: object, statusCode: string|number|null, isEmpty: boolean}>}
 */
export async function fetchDealsWithProperties(params = {}, options = {}) {
  const { customer } = params;
  const { onRaw } = options;

  // Phase 1: Fetch normalized deals
  const result = await fetchDealsFromApi(params);
  const deals = result.data || [];

  // Allow caller to receive raw list first (Phase 1) so UI can render immediately
  if (typeof onRaw === "function") {
    onRaw(result);
  }

  if (deals.length === 0) return result;

  // Phase 2: Extract unique property IDs and enrich
  const propertyIds = [...new Set(
    deals.map((d) => d.property_id).filter(Boolean).map(sanitizePropertyIdForBatch)
  )];

  if (propertyIds.length === 0) {
    return {
      ...result,
      data: deals.map((deal) => (deal.isEnriched != null ? deal : { ...deal, isEnriched: false })),
    };
  }

  try {
    const customerPropertyMap = { [customer]: propertyIds };
    const propertiesDict = await batchFetchPropertiesByCustomer(customerPropertyMap);

    const enrichedDeals = deals.map((deal) => {
      const propertyId = deal.property_id;
      const sanitizedId = sanitizePropertyIdForBatch(propertyId);
      const propertyDetails =
        propertiesDict[sanitizedId] ||
        propertiesDict[propertyId] ||
        (sanitizedId ? propertiesDict[`property#${sanitizedId}`] : null) ||
        null;
      return mergeDealWithProperty(deal, propertyDetails);
    });

    return {
      ...result,
      data: enrichedDeals,
    };
  } catch (err) {
    if (typeof console !== "undefined" && console.error) {
      console.error("[dealsApi] Enrichment orchestration failed", err);
    }
    // Mark hydration attempted so list maps do not wait forever on a failed batch.
    return {
      ...result,
      data: deals.map((deal) => (deal.isEnriched != null ? deal : { ...deal, isEnriched: false })),
    };
  }
}

/**
 * Fetches DealLens rows from GraphQL and normalizes them for list consumption.
...
 * @param {object} params - Request and filter params
 * @returns {Promise<{data: object[], pagination: object, statusCode: string|number|null, isEmpty: boolean}>}
 */
export async function fetchDealsFromApi(params = {}) {
  console.log("[dealsApi] fetchDealsFromApi called", { params: { ...params } });

  const query = getAllDealLensQueryDocument();
  const variables = buildGetAllDealLensVariables(params);
  console.log("[dealsApi] GraphQL variables", { variables });

  const response = await graphqlRequest({
    query,
    variables,
    authMode: "AMAZON_COGNITO_USER_POOLS",
  });

  const apiResult = response?.data?.getAllDealLens;
  const statusCode = apiResult?.statusCode ?? null;
  console.log("[dealsApi] raw response", { statusCode, hasBody: Boolean(apiResult?.body) });

  if (Number(statusCode) === 204) {
    console.log("[dealsApi] 204 No Content, returning empty");
    return {
      data: [],
      pagination: { has_next: false, next_page: null },
      statusCode,
      isEmpty: true,
    };
  }

  // Backend may return body as string (per schema) or as parsed object; normalize to string.
  const rawBody = apiResult?.body;
  const bodyForExtract =
    typeof rawBody === "string" ? rawBody : typeof rawBody === "object" && rawBody !== null ? JSON.stringify(rawBody) : "{}";
  const bodyString = extractBodyString(bodyForExtract);
  const parsedBody = safeParseJson(bodyString);
  const rows = extractDealRows(parsedBody);

  if (IS_DEV) {
    console.log("[dealsApi] RAW parsed body (full response shape):", parsedBody);
    if (rows.length > 0) {
      console.log("[dealsApi] RAW first deal row (field names/shape):", rows[0]);
    }
  }
  console.log("[dealsApi] parsed body", { rowCount: rows.length, pagination: extractPagination(parsedBody) });

  const normalizedRows = rows.map((row, idx) => normalizeDealRow(row, idx));
  console.log("[dealsApi] normalized deals", { count: normalizedRows.length });

  return {
    data: normalizedRows,
    pagination: extractPagination(parsedBody),
    statusCode,
    isEmpty: normalizedRows.length === 0,
  };
}

/**
 * Loads a single DealLens thread via getDealLens (shared by MAO store and opportunities prefetch).
 *
 * @param {{ deallens_id: string, sender: string, user_id: string, customer: string }} params
 * @returns {Promise<{ success: boolean, deal?: object, message?: string, isNotFound?: boolean, isForbidden?: boolean }>}
 */
/**
 * Extracts DealLens negotiation-thread rows from getDeallensThread bodies.
 * Thread payloads may nest history under `thread` while `items` only contains the latest row.
 *
 * @param {*} parsedBody
 * @returns {object[]}
 */
export function extractThreadDealRows(parsedBody) {
  if (Array.isArray(parsedBody)) return parsedBody;
  if (!parsedBody || typeof parsedBody !== "object") return [];

  const candidates = [parsedBody.thread, parsedBody.items, parsedBody.data, parsedBody.deals].filter(
    (value) => Array.isArray(value)
  );
  if (candidates.length > 0) {
    return candidates.reduce((best, current) => (current.length > best.length ? current : best));
  }

  if (parsedBody.deallens_id || parsedBody.id || parsedBody.calculation || parsedBody.status) {
    return [parsedBody];
  }
  return [];
}

/**
 * Fetches DealLens negotiation history for a thread + collection (paginated).
 * SECURITY: Uses AMAZON_COGNITO_USER_POOLS; caller must supply authorized customer context.
 *
 * `nextPage` is an opaque DynamoDB LastEvaluatedKey cursor from a prior response —
 * round-trip unchanged; do not reshape.
 *
 * @param {object} params
 * @param {string} params.thread
 * @param {string} params.collectionId
 * @param {string} params.customer
 * @param {object|null} [params.nextPage] - Opaque cursor; omit/null for first page
 * @returns {Promise<{ data: object[], pagination: { has_next: boolean, next_page: object|null }, statusCode: string|number|null, isEmpty: boolean }>}
 */
export async function fetchDeallensThread({ thread, collectionId, customer, nextPage = null }) {
  const safeThread = String(thread ?? "").trim();
  const safeCollectionId = String(collectionId ?? "").trim();
  const safeCustomer = String(customer ?? "").trim();

  if (!safeThread || !safeCollectionId || !safeCustomer) {
    throw new Error("fetchDeallensThread: thread, collectionId, and customer are required");
  }

  const query = graphQlQueries?.getDeallensThread;
  if (typeof query !== "string" || query.length === 0) {
    throw new Error("Missing generated getDeallensThread query. Run `amplify push` and regenerate.");
  }

  // Always send next_page: null on page 1 (some resolvers reject a missing AWSJSON arg).
  // Page 2+: stringify the opaque LastEvaluatedKey dict (PropertyList / AWSJSON pattern).
  const variables = {
    thread: safeThread,
    collection_id: safeCollectionId,
    customer: safeCustomer,
    next_page:
      nextPage === null || nextPage === undefined || nextPage === ""
        ? null
        : typeof nextPage === "string"
          ? nextPage
          : JSON.stringify(nextPage),
  };

  const response = await graphqlRequest({
    query,
    variables,
    authMode: "AMAZON_COGNITO_USER_POOLS",
  });

  const apiResult = response?.data?.getDeallensThread;
  const statusCode = apiResult?.statusCode ?? null;
  const emptyPagination = { has_next: false, next_page: null };

  if (Number(statusCode) === 204) {
    return { data: [], pagination: emptyPagination, statusCode, isEmpty: true };
  }

  const rawBody = apiResult?.body;
  const bodyForExtract =
    typeof rawBody === "string"
      ? rawBody
      : typeof rawBody === "object" && rawBody !== null
        ? JSON.stringify(rawBody)
        : "{}";
  const bodyString = extractBodyString(bodyForExtract);
  const parsedBody = safeParseJson(bodyString);
  const rows = extractThreadDealRows(parsedBody);

  if (Number(statusCode) >= 400) {
    const message =
      typeof parsedBody === "string"
        ? parsedBody
        : parsedBody?.message || parsedBody?.error || "Failed to load offer thread.";
    throw new Error(String(message));
  }

  const data = rows.map((row, idx) => normalizeDealRow(row, idx));
  return {
    data,
    pagination: extractPagination(parsedBody),
    statusCode,
    isEmpty: data.length === 0,
  };
}

/**
 * Extracts Message rows from getDeallensMessages bodies.
 *
 * @param {*} parsedBody
 * @returns {object[]}
 */
export function extractMessageRows(parsedBody) {
  if (Array.isArray(parsedBody)) return parsedBody;
  if (!parsedBody || typeof parsedBody !== "object") return [];
  if (Array.isArray(parsedBody.items)) return parsedBody.items;
  if (Array.isArray(parsedBody.messages)) return parsedBody.messages;
  if (Array.isArray(parsedBody.data)) return parsedBody.data;
  return [];
}

/**
 * Normalizes one DEALLENS channel Message row for chat consumption.
 *
 * @param {object} row
 * @param {number} index
 * @returns {object}
 */
export function normalizeMessageRow(row, index) {
  const metadata = safeParseJson(row?.metadata);
  const normalizedMetadata =
    metadata && typeof metadata === "object" && !Array.isArray(metadata) ? metadata : {};
  const id =
    String(row?.id ?? row?.message_id ?? row?.sk ?? `msg-${Date.now()}-${index}`).trim() ||
    `msg-${Date.now()}-${index}`;

  return {
    id,
    message: typeof row?.message === "string" ? row.message : "",
    sender: row?.sender ?? "",
    receiver: row?.receiver ?? "",
    // Backend stamps the sender's role on `role` for DEALLENS channel messages.
    sender_role: row?.sender_role ?? row?.role ?? "",
    receiver_role: row?.receiver_role ?? "",
    collection_id:
      typeof row?.collection_id === "string" && row.collection_id.trim()
        ? row.collection_id.trim()
        : null,
    insert_date:
      normalizeTimestamp(row?.insert_date || row?.created_at || null) ??
      row?.insert_date ??
      row?.created_at ??
      "",
    metadata: normalizedMetadata,
    raw: row,
  };
}

/**
 * Fetches DealLens conversation history as Message rows (paginated).
 * SECURITY: Uses AMAZON_COGNITO_USER_POOLS; caller must supply authorized customer context.
 *
 * @param {object} params
 * @param {string} params.collectionId
 * @param {string} params.customer
 * @param {string} [params.campaign] - Optional trio thread hash for tighter scoping
 * @param {object|null} [params.nextPage] - Opaque DynamoDB cursor from a prior response
 * @returns {Promise<{ data: object[], pagination: { has_next: boolean, next_page: object|null }, statusCode: string|number|null, isEmpty: boolean }>}
 */
export async function fetchDeallensMessages({ collectionId, customer, campaign = null, nextPage = null }) {
  const safeCollectionId = String(collectionId ?? "").trim();
  const safeCustomer = String(customer ?? "").trim();
  const safeCampaign = String(campaign ?? "").trim();

  if (!safeCollectionId || !safeCustomer) {
    throw new Error("fetchDeallensMessages: collectionId and customer are required");
  }

  const query = graphQlQueries?.getDeallensMessages;
  if (typeof query !== "string" || query.length === 0) {
    throw new Error("Missing generated getDeallensMessages query. Run `amplify push` and regenerate.");
  }

  const variables = {
    collection_id: safeCollectionId,
    customer: safeCustomer,
    ...(safeCampaign ? { campaign: safeCampaign } : {}),
    next_page:
      nextPage === null || nextPage === undefined || nextPage === ""
        ? null
        : typeof nextPage === "string"
          ? nextPage
          : JSON.stringify(nextPage),
  };

  const response = await graphqlRequest({
    query,
    variables,
    authMode: "AMAZON_COGNITO_USER_POOLS",
  });

  const apiResult = response?.data?.getDeallensMessages;
  const statusCode = apiResult?.statusCode ?? null;
  const emptyPagination = { has_next: false, next_page: null };

  if (Number(statusCode) === 204) {
    return { data: [], pagination: emptyPagination, statusCode, isEmpty: true };
  }

  const rawBody = apiResult?.body;
  const bodyForExtract =
    typeof rawBody === "string"
      ? rawBody
      : typeof rawBody === "object" && rawBody !== null
        ? JSON.stringify(rawBody)
        : "{}";
  const bodyString = extractBodyString(bodyForExtract);
  const parsedBody = safeParseJson(bodyString);
  const rows = extractMessageRows(parsedBody);

  if (Number(statusCode) >= 400) {
    const message =
      typeof parsedBody === "string"
        ? parsedBody
        : parsedBody?.message || parsedBody?.error || "Failed to load conversation messages.";
    throw new Error(String(message));
  }

  const data = rows.map((row, idx) => normalizeMessageRow(row, idx));
  return {
    data,
    pagination: extractPagination(parsedBody),
    statusCode,
    isEmpty: data.length === 0,
  };
}

export async function fetchDealLensByThread({ deallens_id, sender, user_id, customer }) {
  const id = typeof deallens_id === "string" ? deallens_id.trim() : "";
  const s = typeof sender === "string" ? sender.trim() : "";
  const u = typeof user_id === "string" ? user_id.trim() : "";
  const c = typeof customer === "string" ? customer.trim() : "";
  if (!id || !s || !u || !c) {
    return { success: false, message: "Missing deallens_id, sender, user_id, or customer." };
  }

  try {
    const response = await graphqlRequest({
      query: graphQlQueries.getDealLens,
      variables: { deallens_id: id, sender: s, user_id: u, customer: c },
      authMode: "AMAZON_COGNITO_USER_POOLS",
    });
    const graphQlErrors = Array.isArray(response?.errors) ? response.errors : [];
    if (graphQlErrors.length > 0) {
      const messages = graphQlErrors.map((e) => e?.message).filter(Boolean);
      return { success: false, message: messages.join("; ") || "Failed to load deal." };
    }

    const apiResult = response?.data?.getDealLens;
    const statusCode = apiResult?.statusCode != null ? String(apiResult.statusCode) : "";
    const body = apiResult?.body;
    if (Number(statusCode) === 404 || body == null) {
      return { success: false, message: "Deal not found.", isNotFound: true };
    }
    if (Number(statusCode) === 403) {
      return {
        success: false,
        message: typeof body === "string" ? body : "Access denied.",
        isNotFound: false,
        isForbidden: true,
      };
    }
    if (Number(statusCode) < 200 || Number(statusCode) >= 300) {
      return {
        success: false,
        message: typeof body === "string" ? body : "Failed to load deal.",
        isNotFound: false,
      };
    }

    let deal = null;
    if (typeof body === "string") {
      try {
        deal = JSON.parse(body);
      } catch {
        return { success: false, message: "Invalid deal response." };
      }
    } else if (body && typeof body === "object") {
      deal = body;
    }
    if (!deal || typeof deal !== "object") {
      return { success: false, message: "Deal not found.", isNotFound: true };
    }
    return { success: true, deal: normalizeDealRow(deal, 0) };
  } catch (error) {
    const msg = error?.message ?? error?.toString?.() ?? "Unknown error";
    return { success: false, message: msg };
  }
}

/**
 * Loads thread history and returns the tip (last item) for reconcile after append.
 *
 * @param {{ deallens_id: string, user_email: string, customer: string }} params
 * @returns {Promise<{ success: boolean, deal?: object, message?: string }>}
 */
export async function fetchDealLensThreadTip({ deallens_id, user_email, customer }) {
  const id = typeof deallens_id === "string" ? deallens_id.trim() : "";
  const email = typeof user_email === "string" ? user_email.trim() : "";
  const c = typeof customer === "string" ? customer.trim() : "";
  if (!id || !email || !c) {
    return { success: false, message: "Missing deallens_id, user_email, or customer." };
  }

  try {
    const response = await graphqlRequest({
      query: graphQlQueries.getDealLensThread,
      variables: { deallens_id: id, user_email: email, customer: c },
      authMode: "AMAZON_COGNITO_USER_POOLS",
    });
    const graphQlErrors = Array.isArray(response?.errors) ? response.errors : [];
    if (graphQlErrors.length > 0) {
      const messages = graphQlErrors.map((e) => e?.message).filter(Boolean);
      return { success: false, message: messages.join("; ") || "Failed to load thread." };
    }

    const apiResult = response?.data?.getDealLensThread;
    const statusCode = apiResult?.statusCode != null ? String(apiResult.statusCode) : "";
    const body = apiResult?.body;
    if (Number(statusCode) === 404 || body == null) {
      return { success: false, message: "Thread not found." };
    }
    if (Number(statusCode) < 200 || Number(statusCode) >= 300) {
      return {
        success: false,
        message: typeof body === "string" ? body : "Failed to load thread.",
      };
    }

    let parsed = body;
    if (typeof body === "string") {
      try {
        parsed = JSON.parse(body);
      } catch {
        return { success: false, message: "Invalid thread response." };
      }
    }
    const items = Array.isArray(parsed?.items) ? parsed.items : Array.isArray(parsed?.thread) ? parsed.thread : [];
    if (items.length === 0) {
      return { success: false, message: "Thread is empty." };
    }
    const tipRow = items[items.length - 1];
    return { success: true, deal: normalizeDealRow(tipRow, items.length - 1) };
  } catch (error) {
    const msg = error?.message ?? error?.toString?.() ?? "Unknown error";
    return { success: false, message: msg };
  }
}
